import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  ViewColumnsIcon,
  Squares2X2Icon,
  ChevronDownIcon,
  CalendarIcon,
  UserIcon,
  EyeIcon,
  ClipboardDocumentCheckIcon,
  FolderIcon,
  PlusIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const FormsLibrary = () => {
  const [forms, setForms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // Simulate fetching forms data
    const fetchData = () => {
      const mockCategories = [
        {
          id: '1',
          name: 'Patient Forms',
          slug: 'patient-forms',
          description: 'Forms for patient registration and medical history',
          formCount: 12,
          icon: '🏥',
          color: 'bg-blue-100 text-blue-800'
        },
        {
          id: '2',
          name: 'HR Forms',
          slug: 'hr-forms',
          description: 'Human resources and employee forms',
          formCount: 8,
          icon: '👥',
          color: 'bg-green-100 text-green-800'
        },
        {
          id: '3',
          name: 'Administrative',
          slug: 'administrative',
          description: 'Administrative and operational forms',
          formCount: 15,
          icon: '📋',
          color: 'bg-purple-100 text-purple-800'
        },
        {
          id: '4',
          name: 'Financial',
          slug: 'financial',
          description: 'Financial and billing forms',
          formCount: 6,
          icon: '💰',
          color: 'bg-yellow-100 text-yellow-800'
        },
        {
          id: '5',
          name: 'Research',
          slug: 'research',
          description: 'Research and clinical trial forms',
          formCount: 4,
          icon: '🔬',
          color: 'bg-indigo-100 text-indigo-800'
        }
      ];

      const mockForms = [
        {
          id: 1,
          title: 'Patient Registration Form',
          description: 'Comprehensive patient registration form for new admissions',
          category: 'Patient Forms',
          status: 'active',
          version: '2.1',
          createdDate: '2025-01-20',
          lastModified: '2025-01-22',
          createdBy: 'Dr. Sarah Johnson',
          responses: 145,
          views: 289,
          fields: 12,
          estimatedTime: '10 minutes',
          isRequired: true,
          tags: ['Registration', 'Patient', 'Medical History']
        },
        {
          id: 2,
          title: 'Employee Onboarding Checklist',
          description: 'Complete checklist for new employee onboarding process',
          category: 'HR Forms',
          status: 'active',
          version: '1.5',
          createdDate: '2025-01-18',
          lastModified: '2025-01-19',
          createdBy: 'Jennifer Wu',
          responses: 34,
          views: 78,
          fields: 8,
          estimatedTime: '15 minutes',
          isRequired: true,
          tags: ['HR', 'Onboarding', 'Checklist']
        },
        {
          id: 3,
          title: 'Incident Report Form',
          description: 'Report form for workplace incidents and safety concerns',
          category: 'Administrative',
          status: 'active',
          version: '3.0',
          createdDate: '2025-01-15',
          lastModified: '2025-01-16',
          createdBy: 'Lisa Rodriguez',
          responses: 23,
          views: 67,
          fields: 15,
          estimatedTime: '8 minutes',
          isRequired: false,
          tags: ['Incident', 'Safety', 'Report']
        },
        {
          id: 4,
          title: 'Equipment Request Form',
          description: 'Form for requesting new medical equipment and supplies',
          category: 'Administrative',
          status: 'draft',
          version: '1.0',
          createdDate: '2025-01-12',
          lastModified: '2025-01-14',
          createdBy: 'Dr. Michael Chen',
          responses: 0,
          views: 12,
          fields: 10,
          estimatedTime: '5 minutes',
          isRequired: false,
          tags: ['Equipment', 'Request', 'Medical']
        },
        {
          id: 5,
          title: 'Expense Reimbursement Form',
          description: 'Form for submitting business expense reimbursement requests',
          category: 'Financial',
          status: 'active',
          version: '2.0',
          createdDate: '2025-01-10',
          lastModified: '2025-01-11',
          createdBy: 'David Kim',
          responses: 89,
          views: 156,
          fields: 7,
          estimatedTime: '6 minutes',
          isRequired: true,
          tags: ['Finance', 'Reimbursement', 'Expenses']
        },
        {
          id: 6,
          title: 'Clinical Trial Consent Form',
          description: 'Informed consent form for clinical trial participants',
          category: 'Research',
          status: 'review',
          version: '1.3',
          createdDate: '2025-01-08',
          lastModified: '2025-01-09',
          createdBy: 'Dr. Emily Davis',
          responses: 12,
          views: 34,
          fields: 20,
          estimatedTime: '20 minutes',
          isRequired: true,
          tags: ['Research', 'Consent', 'Clinical Trial']
        }
      ];

      setCategories(mockCategories);
      setForms(mockForms);
      setLoading(false);
    };

    setTimeout(fetchData, 500);
  }, []);

  const filteredForms = forms.filter(form => {
    const matchesSearch = form.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         form.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         form.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = !selectedCategory || form.category === selectedCategory;
    const matchesStatus = !selectedStatus || form.status === selectedStatus;

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const sortedForms = [...filteredForms].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'title':
        comparison = a.title.localeCompare(b.title);
        break;
      case 'category':
        comparison = a.category.localeCompare(b.category);
        break;
      case 'responses':
        comparison = a.responses - b.responses;
        break;
      case 'views':
        comparison = a.views - b.views;
        break;
      default: // date
        comparison = new Date(a.lastModified) - new Date(b.lastModified);
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const uniqueCategories = [...new Set(forms.map(form => form.category))];
  const statusOptions = [
    { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800' },
    { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-800' },
    { value: 'review', label: 'Under Review', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'archived', label: 'Archived', color: 'bg-red-100 text-red-800' }
  ];

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(option => option.value === status);
    return statusOption ? statusOption.color : 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const statusOption = statusOptions.find(option => option.value === status);
    return statusOption ? statusOption.label : status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Custom Header for Forms Library */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Forms Library</h1>
                <p className="mt-1 sm:mt-2 text-sm text-gray-700">
                  Browse and manage organizational forms and workflows
                </p>
              </div>
              <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                <Link
                  to="/forms/builder"
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <DocumentTextIcon className="-ml-1 mr-2 h-4 w-4" />
                  Form Builder
                </Link>
                <Link
                  to="/forms/create"
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                  Create Form
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Forms</dt>
                  <dd className="text-lg font-medium text-gray-900">{forms.length}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Forms</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {forms.filter(form => form.status === 'active').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClipboardDocumentCheckIcon className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Responses</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {forms.reduce((sum, form) => sum + form.responses, 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ArrowTrendingUpIcon className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Categories</dt>
                  <dd className="text-lg font-medium text-gray-900">{categories.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Categories Grid */}
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Browse by Category</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.name)}
                className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                  selectedCategory === category.name
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="text-2xl mb-2">{category.icon}</div>
                  <h3 className="font-medium text-gray-900">{category.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{category.formCount} forms</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search forms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center space-x-4">
              {/* Filters Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
                Filters
              </button>

              {/* Sort */}
              <div className="relative">
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                  }}
                  className="block appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm leading-5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="date-desc">Recently Updated</option>
                  <option value="date-asc">Oldest First</option>
                  <option value="title-asc">Title A-Z</option>
                  <option value="title-desc">Title Z-A</option>
                  <option value="category-asc">Category A-Z</option>
                  <option value="responses-desc">Most Responses</option>
                  <option value="views-desc">Most Views</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <ChevronDownIcon className="h-4 w-4" />
                </div>
              </div>

              {/* View Toggle */}
              <div className="flex rounded-md shadow-sm">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`${
                    viewMode === 'grid'
                      ? 'bg-blue-50 border-blue-500 text-blue-600'
                      : 'bg-white border-gray-300 text-gray-500 hover:text-gray-700'
                  } relative inline-flex items-center px-3 py-2 rounded-l-md border text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                >
                  <Squares2X2Icon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`${
                    viewMode === 'list'
                      ? 'bg-blue-50 border-blue-500 text-blue-600'
                      : 'bg-white border-gray-300 text-gray-500 hover:text-gray-700'
                  } relative inline-flex items-center px-3 py-2 -ml-px rounded-r-md border text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                >
                  <ViewColumnsIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Categories</option>
                    {uniqueCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Status</option>
                    {statusOptions.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSelectedCategory('');
                      setSelectedStatus('');
                      setSearchTerm('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Forms Grid/List */}
        {sortedForms.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No forms found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search or filter criteria.
            </p>
            <div className="mt-6">
              <Link
                to="/forms/create"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                Create New Form
              </Link>
            </div>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedForms.map((form) => (
              <div key={form.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center flex-1">
                      <DocumentTextIcon className="h-8 w-8 text-blue-500" />
                      <div className="ml-3 flex-1 min-w-0">
                        <Link
                          to={`/forms/${form.id}`}
                          className="text-lg font-medium text-gray-900 hover:text-blue-600 block truncate"
                        >
                          {form.title}
                        </Link>
                        <p className="text-sm text-gray-500">v{form.version}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-2">
                      {form.isRequired && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      )}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(form.status)}`}>
                        {getStatusLabel(form.status)}
                      </span>
                    </div>
                  </div>
                  
                  <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                    {form.description}
                  </p>
                  
                  <div className="mt-4 flex items-center text-sm text-gray-500">
                    <FolderIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                    {form.category}
                  </div>
                  
                  <div className="mt-2 flex items-center text-sm text-gray-500">
                    <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                    Created by {form.createdBy}
                  </div>
                  
                  <div className="mt-2 flex items-center text-sm text-gray-500">
                    <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                    {form.estimatedTime} • {form.fields} fields
                  </div>
                  
                  <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <EyeIcon className="h-4 w-4 mr-1" />
                        {form.views}
                      </div>
                      <div className="flex items-center">
                        <ClipboardDocumentCheckIcon className="h-4 w-4 mr-1" />
                        {form.responses}
                      </div>
                    </div>
                    <CalendarIcon className="h-4 w-4" />
                  </div>
                  
                  {form.tags && (
                    <div className="mt-4 flex flex-wrap gap-1">
                      {form.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="divide-y divide-gray-200">
              {sortedForms.map((form) => (
                <div key={form.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1 min-w-0">
                      <DocumentTextIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
                      <div className="ml-4 flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Link
                              to={`/forms/${form.id}`}
                              className="text-lg font-medium text-gray-900 hover:text-blue-600 truncate"
                            >
                              {form.title}
                            </Link>
                            {form.isRequired && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                Required
                              </span>
                            )}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(form.status)}`}>
                              {getStatusLabel(form.status)}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500">v{form.version}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{form.description}</p>
                        <div className="mt-2 flex items-center space-x-6 text-sm text-gray-500">
                          <div className="flex items-center">
                            <FolderIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                            {form.category}
                          </div>
                          <div className="flex items-center">
                            <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                            {form.createdBy}
                          </div>
                          <div className="flex items-center">
                            <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                            {form.estimatedTime}
                          </div>
                          <div className="flex items-center">
                            <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                            {form.lastModified}
                          </div>
                          <div className="flex items-center">
                            <EyeIcon className="h-4 w-4 mr-1" />
                            {form.views}
                          </div>
                          <div className="flex items-center">
                            <ClipboardDocumentCheckIcon className="h-4 w-4 mr-1" />
                            {form.responses} responses
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default FormsLibrary;
